import React, { useState, useEffect, useRef } from 'react';
import { API_ENDPOINTS, API_BASE_URL } from '../../config/api';
import axios from '../../config/axiosConfig';
import Toast from '../Toast';
import ConfirmDialog from '../ConfirmDialog';
import LoadingOverlay from '../LoadingOverlay';
import { normalizeRestaurantId } from '../../utils/restaurantUtils';

const WaiterLiveOrders = ({ waiterId, restaurantId: restaurantIdProp }) => {
    // Normalize restaurantId to ensure it's always a string
    const restaurantId = normalizeRestaurantId(restaurantIdProp);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [actionLoading, setActionLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [confirmDialog, setConfirmDialog] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => {},
        confirmText: 'Confirm',
        confirmColor: 'blue'
    });
    const [checkedInWaiters, setCheckedInWaiters] = useState([]);
    const eventSourceRef = useRef(null);

    useEffect(() => {
        if (waiterId && restaurantId) {
            fetchOrders();
            setupSSE();
        }

        return () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
            }
        };
    }, [waiterId, restaurantId]);

    useEffect(() => {
        if (restaurantId) {
            axios.get(API_ENDPOINTS.STAFF_CHECKED_IN(restaurantId), { params: { role: 'WAITER' } })
                .then((res) => setCheckedInWaiters(res.data || []))
                .catch(() => setCheckedInWaiters([]));
        }
    }, [restaurantId]);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            // Fetch today's orders only: assigned to this waiter + unassigned (API: today=true)
            const response = await axios.get(API_ENDPOINTS.ORDERS_WAITER(restaurantId), {
                params: {
                    waiterId,
                    status: 'all',
                    includeUnassigned: 'true',
                    today: 'true',
                }
            });
            const startOfToday = new Date();
            startOfToday.setHours(0, 0, 0, 0);
            const endOfToday = new Date(startOfToday);
            endOfToday.setDate(endOfToday.getDate() + 1);
            const activeOrders = (response.data || []).filter((order) => {
                if (!['PLACED', 'ACCEPTED', 'PREPARING', 'READY', 'SERVED'].includes(order.status)) {
                    return false;
                }
                const orderTime = order?.orderDate || order?.createdAt;
                if (!orderTime) return false;
                const t = new Date(orderTime).getTime();
                if (Number.isNaN(t)) return false;
                return t >= startOfToday.getTime() && t < endOfToday.getTime();
            });
            activeOrders.sort(
                (a, b) =>
                    new Date(b.orderDate || b.createdAt) - new Date(a.orderDate || a.createdAt)
            );
            setOrders(activeOrders);
        } catch (error) {
            console.error('Failed to fetch orders:', error);
            showToast('Failed to load orders', 'error');
        } finally {
            setLoading(false);
        }
    };

    const setupSSE = () => {
        try {
            eventSourceRef.current = new EventSource(`${API_BASE_URL}/orders/notifications/${restaurantId}`);
            
            eventSourceRef.current.onmessage = (event) => {
                const data = JSON.parse(event.data);
                if (data.type === 'NEW_ORDER' || data.type === 'ORDER_UPDATE') {
                    fetchOrders(); // Refresh orders on update
                    
                    // Check if this order is assigned to current waiter
                    if (data.data && data.data.assignedWaiterId) {
                        const assignedWaiterId = typeof data.data.assignedWaiterId === 'object' 
                            ? data.data.assignedWaiterId._id || data.data.assignedWaiterId 
                            : data.data.assignedWaiterId;
                        
                        if (assignedWaiterId === waiterId) {
                            if (data.type === 'NEW_ORDER' || (data.type === 'ORDER_UPDATE' && data.updateType === 'waiter_assigned')) {
                                showToast(`New order #${data.data._id.slice(-6)} assigned to you!`, 'success');
                            } else if (data.type === 'ORDER_UPDATE' && data.updateType === 'status_update') {
                                const orderStatus = data.data.status;
                                if (orderStatus === 'READY') {
                                    showToast(`Order #${data.data._id.slice(-6)} is ready to serve!`, 'success');
                                }
                            }
                        }
                    }
                }
            };

            eventSourceRef.current.onerror = (error) => {
                console.error('SSE connection error:', error);
                // Reconnect after 5 seconds
                setTimeout(() => {
                    if (eventSourceRef.current) {
                        eventSourceRef.current.close();
                    }
                    setupSSE();
                }, 5000);
            };
        } catch (error) {
            console.error('Failed to setup SSE:', error);
        }
    };

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'PLACED':
                return 'bg-gray-100 text-gray-800';
            case 'ACCEPTED':
                return 'bg-blue-100 text-blue-800';
            case 'PREPARING':
                return 'bg-yellow-100 text-yellow-800';
            case 'READY':
                return 'bg-green-100 text-green-800';
            case 'SERVED':
                return 'bg-purple-100 text-purple-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'PLACED':
                return '⚪';
            case 'ACCEPTED':
                return '🔵';
            case 'PREPARING':
                return '🟡';
            case 'READY':
                return '🟢';
            case 'SERVED':
                return '🟣';
            default:
                return '⚪';
        }
    };

    const getItemStatusColor = (status) => {
        switch (status) {
            case 'PENDING':
                return 'bg-gray-100 text-gray-700';
            case 'COOKING':
                return 'bg-orange-100 text-orange-700';
            case 'PREPARING':
                return 'bg-yellow-100 text-yellow-700';
            case 'READY':
                return 'bg-green-100 text-green-700';
            case 'SERVED':
                return 'bg-blue-100 text-blue-700';
            case 'DONE':
                return 'bg-purple-100 text-purple-700';
            default:
                return 'bg-gray-100 text-gray-700';
        }
    };

    const getItemStatusIcon = (status) => {
        switch (status) {
            case 'PENDING':
                return '⏳';
            case 'COOKING':
                return '👨‍🍳';
            case 'PREPARING':
                return '⏰';
            case 'READY':
                return '✅';
            case 'SERVED':
                return '🍽️';
            case 'DONE':
                return '✔️';
            default:
                return '⏳';
        }
    };

    const isOrderAssignedToMe = (order) => {
        const aid = order.assignedWaiterId && (typeof order.assignedWaiterId === 'object' ? order.assignedWaiterId._id : order.assignedWaiterId);
        return aid === waiterId;
    };

    const handleAssignToMe = async (orderId) => {
        setActionLoading(true);
        setLoadingMessage('Assigning to you...');
        try {
            await axios.patch(API_ENDPOINTS.ORDER_ASSIGN_WAITER(orderId), { waiterId });
            showToast('Order assigned to you');
            await fetchOrders();
        } catch (err) {
            showToast(err.response?.data?.message || 'Failed to assign', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleReassignWaiter = async (orderId, newWaiterId) => {
        if (!newWaiterId) return;
        setActionLoading(true);
        setLoadingMessage('Reassigning...');
        try {
            await axios.patch(API_ENDPOINTS.ORDER_ASSIGN_WAITER(orderId), { waiterId: newWaiterId });
            showToast('Order reassigned');
            await fetchOrders();
        } catch (err) {
            showToast(err.response?.data?.message || 'Failed to reassign', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleOrderStatusChange = async (orderId, newStatus) => {
        // Show confirmation for critical status changes
        if (newStatus === 'CLOSED') {
            setConfirmDialog({
                isOpen: true,
                title: 'Close Order',
                message: 'Are you sure you want to close this order? This action cannot be undone.',
                confirmText: 'Close Order',
                confirmColor: 'red',
                onConfirm: async () => {
                    setActionLoading(true);
                    setLoadingMessage('Closing order...');
                    try {
                        await axios.patch(API_ENDPOINTS.ORDER_UPDATE_STATUS(orderId), {
                            status: newStatus
                        });
                        showToast('Order status updated successfully');
                        await fetchOrders();
                    } catch (error) {
                        showToast(error.response?.data?.message || 'Failed to update order status', 'error');
                    } finally {
                        setActionLoading(false);
                    }
                }
            });
        } else {
            setActionLoading(true);
            setLoadingMessage('Updating order status...');
            try {
                await axios.patch(API_ENDPOINTS.ORDER_UPDATE_STATUS(orderId), {
                    status: newStatus
                });
                showToast('Order status updated successfully');
                await fetchOrders();
            } catch (error) {
                showToast(error.response?.data?.message || 'Failed to update order status', 'error');
            } finally {
                setActionLoading(false);
            }
        }
    };

    if (loading) {
        return (
            <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
        );
    }

    return (
        <div>
            <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                    <h2 className="text-2xl font-bold">Live Order Status</h2>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={fetchOrders}
                            disabled={loading}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                            title="Refresh orders"
                        >
                            {loading ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            ) : (
                                <span>🔄</span>
                            )}
                            <span className="hidden sm:inline">Refresh</span>
                        </button>
                        <div className="flex items-center gap-2">
                            <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
                            <span className="text-sm text-gray-600">Live Updates</span>
                        </div>
                    </div>
                </div>
                <p className="text-gray-600">Real-time updates on today&apos;s active orders only</p>
            </div>

            {orders.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <span className="text-6xl block mb-4">📋</span>
                    <p className="text-gray-600 text-lg">No active orders for today</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {orders.map((order) => (
                        <div
                            key={order._id}
                            className={`bg-white rounded-lg shadow-md p-6 border-l-4 ${
                                order.status === 'READY' ? 'border-green-500' :
                                order.status === 'PREPARING' ? 'border-yellow-500' :
                                order.status === 'ACCEPTED' ? 'border-blue-500' :
                                'border-gray-300'
                            }`}
                        >
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h3 className="text-lg font-bold">
                                        Order #{order._id.slice(-6)}
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                        Table: {order.tableId?.tableNumber || order.tableNumber || 'N/A'}
                                    </p>
                                    {order.customerName && (
                                        <p className="text-sm text-gray-600">
                                            Customer: {order.customerName}
                                        </p>
                                    )}
                                    <p className="text-xs text-gray-500">
                                        {new Date(order.orderDate).toLocaleString()}
                                    </p>
                                </div>
                                <div className="text-right flex flex-col items-end gap-2">
                                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(order.status)}`}>
                                        {getStatusIcon(order.status)} {order.status}
                                    </span>
                                    {order.status !== 'CLOSED' && (
                                        <select
                                            value={order.status}
                                            onChange={(e) => handleOrderStatusChange(order._id, e.target.value)}
                                            className="text-xs px-2 py-1 border rounded bg-white font-medium cursor-pointer hover:bg-gray-50"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <option value="PLACED">Placed</option>
                                            <option value="ACCEPTED">Accepted</option>
                                            <option value="PREPARING">Preparing</option>
                                            <option value="READY">Ready</option>
                                            <option value="SERVED">Served</option>
                                            <option value="CLOSED">Closed</option>
                                        </select>
                                    )}
                                    {order.status === 'READY' && (
                                        <div className="mt-2 animate-pulse">
                                            <span className="text-xs text-green-600 font-semibold">⚠️ Ready to Serve!</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="mb-4">
                                <p className="text-sm font-semibold mb-2">Items Status:</p>
                                <div className="space-y-2">
                                    {order.items?.map((item, idx) => {
                                        const itemStatus = item.status || 'PENDING';
                                        return (
                                            <div key={idx} className="flex justify-between items-center text-sm bg-gray-50 p-2 rounded">
                                                <div className="flex items-center gap-2 flex-1">
                                                    <span className={getItemStatusColor(itemStatus)}>
                                                        {getItemStatusIcon(itemStatus)}
                                                    </span>
                                                    <span>{item.name} x {item.quantity}</span>
                                                </div>
                                                <span className={`px-2 py-1 rounded text-xs font-semibold ${getItemStatusColor(itemStatus)}`}>
                                                    {itemStatus}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="flex flex-wrap items-center justify-between gap-2 pt-4 border-t">
                                <span className="font-bold">Total: ₹{order.finalAmount || order.totalAmount || 0}</span>
                                <div className="flex items-center gap-2 flex-wrap">
                                    {order.assignedCookId && (
                                        <span className="text-xs text-gray-500">
                                            Cook: {order.assignedCookId.name || 'N/A'}
                                        </span>
                                    )}
                                    <span className="text-xs text-gray-500">
                                        Waiter: {order.assignedWaiterId?.name || 'Not assigned'}
                                    </span>
                                    {!order.assignedWaiterId || !isOrderAssignedToMe(order) ? (
                                        <button
                                            type="button"
                                            onClick={() => handleAssignToMe(order._id)}
                                            disabled={actionLoading}
                                            className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                                        >
                                            Assign to me
                                        </button>
                                    ) : (
                                        checkedInWaiters.length > 1 && (
                                            <select
                                                className="text-xs border rounded px-2 py-1 bg-white"
                                                value=""
                                                onChange={(e) => {
                                                    const id = e.target.value;
                                                    if (id) handleReassignWaiter(order._id, id);
                                                    e.target.value = '';
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <option value="">Reassign to...</option>
                                                {checkedInWaiters.filter((w) => w._id !== waiterId).map((w) => (
                                                    <option key={w._id} value={w._id}>{w.name}</option>
                                                ))}
                                            </select>
                                        )
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {actionLoading && <LoadingOverlay message={loadingMessage} />}
            {toast.show && <Toast message={toast.message} type={toast.type} />}
            <ConfirmDialog
                isOpen={confirmDialog.isOpen}
                onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
                onConfirm={confirmDialog.onConfirm}
                title={confirmDialog.title}
                message={confirmDialog.message}
                confirmText={confirmDialog.confirmText}
                confirmColor={confirmDialog.confirmColor}
            />
        </div>
    );
};

export default WaiterLiveOrders;

